import ownAuthSession from './auth/ownAuthSession';

// Süper admin › İş Kuyruğu: ortak iş kuyruğunun (GtsJobs) ana API uçları.
// Uçlar gts_dotnetapi'de: /api/superadmin/jobs (SuperAdminJobsController, [SuperAdminOnly]).

const NETAPI_URL = process.env.REACT_APP_NETAPI_URL || 'https://gtsnetapi.aquasoft.com.tr';

export type JobView = 'dead' | 'active' | 'done' | 'cancelled';

/** 0 Bekliyor, 1 Çalışıyor, 2 Tamamlandı, 3 Ölü, 4 İptal */
export type JobStatus = 0 | 1 | 2 | 3 | 4;

export interface JobDatabaseSummary {
  database: string;
  available: boolean;
  pending: number;
  running: number;
  dead: number;
  succeededLast24h: number;
  oldestPendingSeconds: number | null;
}

export interface JobSummary {
  databases: JobDatabaseSummary[];
  total: {
    pending: number;
    running: number;
    dead: number;
    succeededLast24h: number;
    oldestPendingSeconds: number | null;
  };
  /** iş türü → ekranda gösterilecek ad */
  jobTypes: Record<string, string>;
}

export interface JobRow {
  db: string;
  id: number;
  queue: string;
  jobType: string;
  jobTypeName: string;
  companyId: string | null;
  companyName: string | null;
  branchId: string | null;
  branchName: string | null;
  reference: string | null;
  status: JobStatus;
  statusName: string;
  attempts: number;
  maxAttempts: number;
  runAfter: string;
  lockedBy: string | null;
  lastError: string | null;
  source: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  payloadJson: string | null;
}

export interface JobListFilters {
  view: JobView;
  db?: string;
  jobType?: string;
  companyId?: string;
  reference?: string;
  take?: number;
}

async function jobsRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await ownAuthSession.getAccessToken();
  if (!token) throw new Error('Oturum açılmamış');

  const response = await fetch(`${NETAPI_URL}/api/superadmin/jobs${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 404 && !(data as { message?: string }).message) {
      throw new Error('Ana API bu ucu tanımıyor; gts_dotnetapi yeni sürümle yayınlanmalı.');
    }
    throw new Error((data as { message?: string }).message || 'İş kuyruğu isteği başarısız');
  }
  return data as T;
}

export function getJobSummary() {
  return jobsRequest<JobSummary & { success: boolean }>('/summary');
}

export function getJobs(filters: JobListFilters) {
  const params = new URLSearchParams();
  params.set('view', filters.view);
  if (filters.db) params.set('db', filters.db);
  if (filters.jobType) params.set('jobType', filters.jobType);
  if (filters.companyId) params.set('companyId', filters.companyId);
  if (filters.reference) params.set('reference', filters.reference);
  params.set('take', String(filters.take ?? 200));
  return jobsRequest<{ success: boolean; jobs: JobRow[]; count: number }>(`?${params.toString()}`);
}

export function retryJob(db: string, id: number) {
  return jobsRequest<{ success: boolean }>(`/${encodeURIComponent(db)}/${id}/retry`, { method: 'POST' });
}

export function cancelJob(db: string, id: number) {
  return jobsRequest<{ success: boolean }>(`/${encodeURIComponent(db)}/${id}/cancel`, { method: 'POST' });
}

export function retryJobsBulk(items: { db: string; id: number }[]) {
  return jobsRequest<{ success: boolean; retried: number; skipped: number }>('/retry-bulk', {
    method: 'POST',
    body: JSON.stringify({ items }),
  });
}
